import type { Dispatch } from '@reduxjs/toolkit';
import type { IVaultDocument } from 'src/legacy/@types/common';
import type {
  IFosterFamilyState,
  IGetCompanyRecordQueryParams,
  IGetFosterFamiliesQueryParams,
  INewAndUpdateFosterFamilyDto,
  INewAndUpdateRegisterLeaderDto,
} from 'src/legacy/@types/fosterFamily';

import { createSlice } from '@reduxjs/toolkit';
import axios from 'src/legacy/utils/axios';

// ----------------------------------------------------------------------

const initialState: IFosterFamilyState = {
  actionStatus: {
    create: 'none',
    delete: 'none',
    document: {
      create: 'none',
      delete: 'none',
      update: 'none',
      upload: 'none',
    },
    registerLeader: {
      create: null,
      delete: 'none',
      update: null,
    },
    update: 'none',
  },
  companyRecords: null,
  error: null,
  fosterFamilies: null,
  isLoading: false,
  records: null,
  registerLeaders: null,
  selectedFosterFamily: null,
  selectedRegisterLeader: null,
  vault: {
    count: 0,
    next: '',
    previous: '',
    results: [],
  },
};

const slice = createSlice({
  initialState,
  name: 'foster-family',
  reducers: {
    // CLEAR COMPANY RECORD LIST
    clearCompanyRecord(state) {
      state.companyRecords = null;
    },

    // CLEAR ERROR
    clearError(state) {
      state.error = '';
    },

    // CLEAR FOSTER FAMILY RECORD LIST
    clearFosterFamilyRecord(state) {
      state.records = null;
    },

    createFosterFamilyDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'failed',
        },
      };
    },

    // CREATE NEW FOSTER FAMILY DOCUMENT
    createFosterFamilyDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'loading',
        },
      };
    },

    createFosterFamilyDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'complete',
        },
      };
      state.error = null;

      if (state.selectedFosterFamily) {
        state.selectedFosterFamily = {
          ...state.selectedFosterFamily,
          documents: [
            ...(state.selectedFosterFamily?.documents || []),
            action.payload,
          ],
        };
      }
    },

    // CREATE NEW FOSTER FAMILY
    createFosterFamilySuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        create: 'complete',
      };
      state.error = null;
      state.fosterFamilies = [...(state.fosterFamilies || []), action.payload];
    },

    createFosterFamilyVaultDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'failed',
        },
      };
    },

    createFosterFamilyVaultDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'loading',
        },
      };
    },

    createFosterFamilyVaultDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          create: 'complete',
        },
      };
      state.error = null;
    },

    createRegisterLeaderFailed(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          create: [
            ...(state.actionStatus.registerLeader.create || []),
            action.payload,
          ],
        },
      };
    },

    // CREATE NEW REGISTER LEADER
    createRegisterLeaderStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          create: null,
        },
      };
    },

    createRegisterLeaderSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          create: null,
        },
      };
      state.error = null;
      state.registerLeaders = [
        ...(state.registerLeaders || []),
        action.payload,
      ];
      if (state.selectedFosterFamily) {
        state.selectedFosterFamily = {
          ...state.selectedFosterFamily,
          register_leaders: [
            ...(state.selectedFosterFamily.register_leaders || []),
            action.payload.id,
          ],
        };
      }
    },

    deleteFosterFamilyDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'failed',
        },
      };
    },

    // UPDATE FOSTER FAMILY DOCUMENT
    deleteFosterFamilyDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'loading',
        },
      };
    },

    deleteFosterFamilyDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'complete',
        },
      };
      state.error = null;

      if (state.selectedFosterFamily) {
        state.selectedFosterFamily = {
          ...state.selectedFosterFamily,
          documents: state.selectedFosterFamily.documents.filter(
            (doc) => doc.id !== action.payload.documentId
          ),
        };
      }
    },

    // DELETE FOSTER FAMILY
    deleteFosterFamilySuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        delete: 'complete',
      };
      if (state.fosterFamilies) {
        state.fosterFamilies = state.fosterFamilies.filter(
          (family) => family.id !== action.payload.familyId
        );
      }
    },

    deleteFosterFamilyVaultDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'failed',
        },
      };
    },

    // DELETE COMPANY VAULT FILE
    deleteFosterFamilyVaultDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'loading',
        },
      };
    },

    deleteFosterFamilyVaultDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          delete: 'complete',
        },
      };
      state.error = null;
    },

    deleteRegisterLeaderFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          delete: 'failed',
        },
      };
    },

    // DELETE REGISTER LEADER
    deleteRegisterLeaderStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          delete: 'loading',
        },
      };
    },

    deleteRegisterLeaderSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          delete: 'complete',
        },
      };
      if (state.registerLeaders) {
        state.registerLeaders = state.registerLeaders.filter(
          (leader) => leader.id !== action.payload.leaderId
        );
      }
    },

    // GET COMPANY RECORD LIST
    getCompanyRecordListSuccess(state, action) {
      state.isLoading = false;
      state.companyRecords = action.payload;
    },

    // GET FOSTER FAMILYS
    getFosterFamiliesSuccess(state, action) {
      state.isLoading = false;
      state.fosterFamilies = action.payload;
    },

    // GET FOSTER FAMILY DOCUMENT LIST
    getFosterFamilyDocumentListSuccess(state, action) {
      // Todo
    },

    // GET FOSTER FAMILY DOCUMENT
    getFosterFamilyDocumentSuccess(state, action) {
      // Todo
    },

    // GET FOSTER FAMILY RECORD
    getFosterFamilyRecordSuccess(state, action) {
      // TODO:
    },

    // GET FOSTER FAMILY
    getFosterFamilySuccess(state, action) {
      state.isLoading = false;
      state.selectedFosterFamily = action.payload;
    },

    // VAULT REDUCER ACTIONS //
    // CREATE NEW CLIENT VAULT FILE
    getFosterFamilyVaultDocumentSuccess(state, action) {
      state.isLoading = false;
      state.vault = action.payload;
    },

    // GET REGISTER LEADER LIST
    getRegisterLeaderListSuccess(state, action) {
      state.isLoading = false;
      state.registerLeaders = action.payload;
    },

    // GET REGISTER LEADER
    getRegisterLeaderSuccess(state, action) {
      state.isLoading = false;
      state.selectedRegisterLeader = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // UPDATE ACTION STATUS
    updateActionState(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        ...action.payload,
      };
    },

    updateFosterFamilyDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'failed',
        },
      };
    },

    // UPDATE FOSTER FAMILY DOCUMENT
    updateFosterFamilyDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'loading',
        },
      };
    },

    updateFosterFamilyDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'complete',
        },
      };
      state.error = null;

      // Todo
    },

    // UPDATE FOSTER FAMILY
    updateFosterFamilySuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        update: 'complete',
      };
      state.error = null;
      if (state.fosterFamilies) {
        state.fosterFamilies = state.fosterFamilies.map((family) => {
          if (family.id === action.payload.id) {
            return action.payload;
          }
          return family;
        });
      }
    },

    updateFosterFamilyVaultFolderFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'failed',
        },
      };
    },

    // UPDATE COMPANY DOCUMENT
    updateFosterFamilyVaultFolderStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'loading',
        },
      };
    },

    updateFosterFamilyVaultFolderSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          update: 'complete',
        },
      };
      state.error = null;
      const list = state.vault.results.map((doc) =>
        doc.id === action.payload.id ? action.payload : doc
      );

      state.vault = {
        ...state.vault,
        results: list,
      };
    },

    updateRegisterLeaderFailed(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          update: [
            ...(state.actionStatus.registerLeader.update || []),
            action.payload,
          ],
        },
      };
    },

    // UPDATE REGISTER LEADER
    updateRegisterLeaderStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          update: null,
        },
      };
    },

    updateRegisterLeaderSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        registerLeader: {
          ...state.actionStatus.registerLeader,
          update: null,
        },
      };
      state.error = null;
      if (state.registerLeaders) {
        state.registerLeaders = state.registerLeaders.map((leader) => {
          if (leader.id === action.payload.id) {
            return action.payload;
          }
          return leader;
        });
      }
    },

    uploadFosterFamilyVaultDocumentFailed(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          upload: 'failed',
        },
      };
    },

    uploadFosterFamilyVaultDocumentStart(state) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          upload: 'loading',
        },
      };
    },

    uploadFosterFamilyVaultDocumentSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        document: {
          ...state.actionStatus.document,
          upload: 'complete',
        },
      };
      state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  clearError,
  getFosterFamilySuccess,
  getRegisterLeaderListSuccess,
  updateActionState,
} = slice.actions;

// ----------------------------------------------------------------------
// --------------           Foster Family Actions          --------------
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

export const getFosterFamilies = (params: IGetFosterFamiliesQueryParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`/care/foster-families/`, { params });
      let allResults: any[] = response.data.results;

      if (
        response.data.count > allResults.length ||
        response.data.next !== ''
      ) {
        await Promise.all(
          Array(Math.ceil(response.data.count / 20) - 1)
            .fill(0)
            .map(async (_, index) => {
              const response2 = await axios.get(`/care/foster-families/`, {
                params: { ...params, page: index + 2 },
              });
              allResults = [...allResults, ...response2.data.results];
            })
        );
      }

      dispatch(slice.actions.getFosterFamiliesSuccess(allResults));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createFosterFamily = (
  companyId: string,
  newFosterFamily: INewAndUpdateFosterFamilyDto
) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch(
      slice.actions.updateActionState({ create: 'loading', error: null })
    );
    try {
      const response = await axios.post(
        `/care/foster-families/?company=${companyId}`,
        {
          ...newFosterFamily,
          register_leaders: [],
        }
      );
      if (
        response.data.id &&
        newFosterFamily.register_leaders &&
        newFosterFamily.register_leaders?.length > 0
      ) {
        await newFosterFamily.register_leaders.forEach(async (leader) => {
          await dispatch(createRegisterLeader(response.data.id, leader));
        });
      }
      dispatch(slice.actions.createFosterFamilySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ create: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getFosterFamily = (familyId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/care/foster-families/${familyId}/`);
      dispatch(slice.actions.getFosterFamilySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateFosterFamily = (
  familyId: number,
  updatedFosterFamily: INewAndUpdateFosterFamilyDto
) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      if (
        updatedFosterFamily.register_leaders &&
        updatedFosterFamily.register_leaders?.length > 0
      ) {
        updatedFosterFamily.register_leaders.forEach((leader) => {
          if (leader.foster_family) {
            if (leader.isDeleted) {
              dispatch(deleteRegisterLeader(familyId, leader.id));
            }
            if (leader.isModified) {
              dispatch(updateRegisterLeader(familyId, leader.id, leader));
            }
          } else {
            dispatch(createRegisterLeader(familyId, leader));
          }
        });
      }
      const response = await axios.put(`/care/foster-families/${familyId}/`, {
        ...updatedFosterFamily,
        register_leaders: [],
      });
      dispatch(slice.actions.updateFosterFamilySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteFosterFamily = (familyId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateActionState({ delete: 'loading' }));
    try {
      await axios.delete(`/care/foster-families/${familyId}/`);
      dispatch(slice.actions.deleteFosterFamilySuccess({ familyId }));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ delete: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Register Leader Actions         --------------
// ----------------------------------------------------------------------

export const getRegisterLeaders = (fosterFamilyId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/foster-families/${fosterFamilyId}/register-leaders/`
      );
      dispatch(
        slice.actions.getRegisterLeaderListSuccess(response.data.results)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createRegisterLeader = (
  fosterFamilyId: number,
  newRegisterLeader: INewAndUpdateRegisterLeaderDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createRegisterLeaderStart());
    try {
      const response = await axios.post(
        `/care/foster-families/${fosterFamilyId}/register-leaders/`,
        newRegisterLeader
      );
      dispatch(slice.actions.createRegisterLeaderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createRegisterLeaderFailed(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getRegisterLeader = (
  fosterFamilyId: number,
  registerLeaderId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/foster-families/${fosterFamilyId}/register-leaders/${registerLeaderId}/`
      );
      dispatch(slice.actions.getRegisterLeaderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateRegisterLeader = (
  fosterFamilyId: number,
  registerLeaderId: number,
  updatedRegisterLeader: INewAndUpdateRegisterLeaderDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateRegisterLeaderStart());
    try {
      const response = await axios.put(
        `/care/foster-families/${fosterFamilyId}/register-leaders/${registerLeaderId}/`,
        updatedRegisterLeader
      );
      dispatch(slice.actions.updateRegisterLeaderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateRegisterLeaderFailed(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteRegisterLeader = (
  fosterFamilyId: number,
  registerLeaderId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteRegisterLeaderStart());
    try {
      await axios.delete(
        `/care/foster-families/${fosterFamilyId}/register-leaders/${registerLeaderId}/`
      );
      dispatch(slice.actions.deleteRegisterLeaderSuccess({ registerLeaderId }));
    } catch (error) {
      dispatch(slice.actions.deleteRegisterLeaderFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------      Foster Family Document Actions      --------------
// ----------------------------------------------------------------------

export const getFosterFamilyDocumentList = (
  fosterFamilyId: string,
  params: IGetFosterFamiliesQueryParams
) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/care/foster-families/${fosterFamilyId}/documents/`,
        {
          params,
        }
      );
      dispatch(
        slice.actions.getFosterFamilyDocumentListSuccess(response.data.results)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createFosterFamilyDocument = (
  fosterFamilyId: string,
  data: any
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createFosterFamilyDocumentStart());
    try {
      const response = await axios.post(
        `/care/foster-families/${fosterFamilyId}/documents/`,
        data,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      );
      dispatch(slice.actions.createFosterFamilyDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.createFosterFamilyDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getFosterFamilyDocument = (
  fosterFamilyId: string,
  documentId: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axios.get(
        `/care/foster-families/${fosterFamilyId}/documents/${documentId}/`
      );
      dispatch(slice.actions.getFosterFamilyDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateFosterFamilyDocument = (
  fosterFamilyId: string,
  documentId: string,
  data: FormData
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateFosterFamilyDocumentStart());
    try {
      const response = await axios.put(
        `/care/foster-families/${fosterFamilyId}/documents/${documentId}/`,
        data,
        {
          headers: { 'content-type': 'multipart/form-data' },
        }
      );
      dispatch(slice.actions.updateFosterFamilyDocumentSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateFosterFamilyDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteFosterFamilyDocument = (
  fosterFamilyId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteFosterFamilyDocumentStart());
    try {
      await axios.delete(
        `/care/foster-families/${fosterFamilyId}/documents/${documentId}/`
      );
      dispatch(slice.actions.deleteFosterFamilyDocumentSuccess({ documentId }));
    } catch (error) {
      dispatch(slice.actions.deleteFosterFamilyDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Company Record Actions          --------------
// ----------------------------------------------------------------------

export const getCompanyRecordList = (params: IGetCompanyRecordQueryParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.clearCompanyRecord());
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`/care/foster-family-records/`, {
        params,
      });
      let allResults: any[] = response.data.results;

      if (
        response.data.count > allResults.length ||
        response.data.next !== ''
      ) {
        await Promise.all(
          Array(Math.ceil(response.data.count / 20) - 1)
            .fill(0)
            .map(async (_, index) => {
              const response2 = await axios.get(
                `/care/foster-family-records/`,
                {
                  params: { ...params, page: index + 2 },
                }
              );
              allResults = [...allResults, ...response2.data.results];
            })
        );
      }

      dispatch(slice.actions.getCompanyRecordListSuccess(allResults));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------
// --------------          Foster Family Vault Actions        --------------
// ----------------------------------------------------------------------

export const getFosterFamilyVault = (clientId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/foster-families/${clientId}/vault/`
      );
      const finalList = response.data.results.map((doc: IVaultDocument) => {
        return {
          ...doc,
          id: `${doc.type}_${doc.id}`,
        };
      });
      dispatch(
        slice.actions.getFosterFamilyVaultDocumentSuccess({
          ...response.data,
          results: finalList,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const uploadFosterFamilyVaultFile = (
  clientId: string,
  data: FormData
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.uploadFosterFamilyVaultDocumentStart());
    try {
      const response = await axios.post(
        `/care/foster-families/${clientId}/vault/files/`,
        data,
        {
          headers: {
            'content-type': 'multipart/form-data',
          },
        }
      );
      dispatch(
        slice.actions.uploadFosterFamilyVaultDocumentSuccess(response.data)
      );
    } catch (error) {
      dispatch(slice.actions.uploadFosterFamilyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteFosterFamilyVaultFile = (
  clientId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteFosterFamilyVaultDocumentStart());
    try {
      await axios.delete(
        `/care/foster-families/${clientId}/vault/files/${documentId}/`
      );
      dispatch(
        slice.actions.deleteFosterFamilyVaultDocumentSuccess({
          documentId,
          fileType: 'file',
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteFosterFamilyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getFosterFamilyVaultFolderContents = (
  clientId: string,
  folderId: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/care/foster-families/${clientId}/vault/folders/${folderId}/contents/`
      );
      const finalList = response.data.results.map((doc: IVaultDocument) => {
        return {
          ...doc,
          id: `${doc.type}_${doc.id}`,
        };
      });
      dispatch(
        slice.actions.getFosterFamilyVaultDocumentSuccess({
          ...response.data,
          results: finalList,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createFosterFamilyVaultFolder = (clientId: string, data: any) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.createFosterFamilyVaultDocumentStart());
    try {
      const response = await axios.post(
        `/care/foster-families/${clientId}/vault/folders/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(
        slice.actions.createFosterFamilyVaultDocumentSuccess(response.data)
      );
    } catch (error) {
      dispatch(slice.actions.createFosterFamilyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateFosterFamilyVaultFolder = (
  clientId: string,
  folderId: string,
  data: any
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateFosterFamilyVaultFolderStart());
    try {
      const response = await axios.put(
        `/care/foster-families/${clientId}/vault/folders/${folderId}/`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );
      dispatch(
        slice.actions.updateFosterFamilyVaultFolderSuccess(response.data)
      );
    } catch (error) {
      dispatch(slice.actions.updateFosterFamilyVaultFolderFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteFosterFamilyVaultFolder = (
  clientId: string,
  documentId: number
) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.deleteFosterFamilyVaultDocumentStart());
    try {
      await axios.delete(
        `/care/foster-families/${clientId}/vault/folders/${documentId}/`
      );
      dispatch(
        slice.actions.deleteFosterFamilyVaultDocumentSuccess({
          documentId,
          fileType: 'folder',
        })
      );
    } catch (error) {
      dispatch(slice.actions.deleteFosterFamilyVaultDocumentFailed());
      dispatch(slice.actions.hasError(error));
    }
  };
};
